import axios, {AxiosResponse} from 'axios';
import {handleError} from './errorHandling';
import {DateRange} from '../stores/TimeIntervalStore';
import {DateRangeWithId, PageLayout, SerializedReport, SerializedReportBuildingBlock, TextVariableWithValue} from '../report/types/Report';
import {TableSort} from '../types/Table';
import {getLanguage} from '../i18n/i18n';

interface CsvRequest {
    interval: DateRange;
    channel: string;
    language: string;
    mode: 'searchterm' | 'product';
    sorting: TableSort;
    separator: string;
}

export function downloadTableCsv(data: CsvRequest) {
    const {mode, ...queryParams} = data;
    return axios
        .get(`factsheet/csv/${mode}`, {
            responseType: 'arraybuffer',
            params: queryParams,
        })
        .catch(handleError('error:downloadCsvFileFailed'));
}

export interface ReportRequest {
    currency: string;
    blocks: SerializedReportBuildingBlock[];
    language?: string;
    layout: PageLayout;
    variables: TextVariableWithValue[];
    dates: DateRangeWithId[];
}

export interface TemplateRequest {
    id: number;
    dates: DateRangeWithId[];
    variables: TextVariableWithValue[];
    language?: string;
}

export interface TemplateUpdateResult {
    id: number;
    lastChanged: number;
}

export function downloadReport(data: ReportRequest) {
    return axios
        .post('factsheet/download', data, {headers: {'processed-data': false}, responseType: 'arraybuffer'})
        .catch(handleError('error:reportCreationFailed'));
}

export function downloadTemplateReport(data: TemplateRequest) {
    const {id, ...params} = data;
    return axios
        .post(`factsheet/template/${id}/download`, params, {headers: {'processed-data': false}, responseType: 'arraybuffer'})
        .catch(handleError('error:reportCreationFailed'));
}

export function getTemplates(): Promise<AxiosResponse<SerializedReport[]>> {
    return axios.get(`factsheet/template`).catch(handleError('error:loadingReportTemplatesFailed'));
}

export function getDefaultTemplates(): Promise<AxiosResponse<SerializedReport[]>> {
    const lang = getLanguage();

    // E.g. de-DE as accepted language code will not match our de language on the server. This is normal HTTP behavior
    // (https://datatracker.ietf.org/doc/html/rfc2616#section-14.4), but getting an English version, which is the default
    // language on the server, for a de-DE tag seems to be worse than getting de. It is also in accordance with the behavior of FF UI.
    const countryCodeIndex = lang ? lang.indexOf('-') : -1;
    const langWithoutCountryCode = countryCodeIndex === -1 ? '' : `,${lang!.substring(0, countryCodeIndex)};q=0.9`;

    const config = lang ? {headers: {'Accept-Language': `${lang};q=1.0${langWithoutCountryCode}`}} : {};

    return axios.get(`factsheet/template/presets`, config).catch(handleError('error:loadingReportTemplatesFailed'));
}

export function updateTemplate(report: SerializedReport, force: boolean): Promise<AxiosResponse<TemplateUpdateResult>> {
    return axios.patch(`factsheet/template`, report, {params: {force}}).catch(handleError('error:updatingTemplateFailed'));
}

export function createTemplate(report: SerializedReport): Promise<AxiosResponse<TemplateUpdateResult>> {
    return axios.post(`factsheet/template`, report).catch(handleError('error:createTemplateFailed'));
}

export function removeTemplate(id: number): Promise<AxiosResponse<string>> {
    return axios.delete(`factsheet/template/${id}`).catch(handleError('error:removingTemplateFailed'));
}



// WEBPACK FOOTER //
// ./src/apis/ReportAPI.ts